import { FeatureFlags, type AnyType, type ApiError } from '@lightdash/common';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { lightdashApi } from '../../../api';
import { useServerFeatureFlag } from '../../../hooks/useServerOrClientFeatureFlag';
import {
    type CreateIntelligenceImport,
    type FeedbackReviewEvent,
    type FeedbackReviewItem,
    type FeedbackReviewList,
    type FeedbackReviewStatus,
    type IntelligenceImport,
    type IntelligenceSource,
} from '../types';

const basePath = (projectUuid: string) =>
    `/projects/${projectUuid}/intelligence`;

// ponytail: remove when intelligence responses join the generated ApiResults union.
const intelligenceApi = <T>(props: Parameters<typeof lightdashApi>[0]) =>
    lightdashApi<AnyType>(props) as Promise<T>;

export const useIntelligenceSources = (projectUuid: string | undefined) =>
    useQuery<IntelligenceSource[], ApiError>({
        queryKey: ['intelligence-sources', projectUuid],
        enabled: Boolean(projectUuid),
        queryFn: () =>
            intelligenceApi<IntelligenceSource[]>({
                url: `${basePath(projectUuid!)}/sources`,
                method: 'GET',
            }),
    });

export type FeedbackFilters = {
    page: number;
    pageSize: number;
    sourceUuid: string | null;
    status: FeedbackReviewStatus | null;
    from: string | null;
    to: string | null;
};

export const useFeedbackReviews = (
    projectUuid: string | undefined,
    filters: FeedbackFilters,
) =>
    useQuery<FeedbackReviewList, ApiError>({
        queryKey: ['intelligence-feedback', projectUuid, filters],
        enabled: Boolean(projectUuid),
        keepPreviousData: true,
        queryFn: () => {
            const query = new URLSearchParams({
                page: String(filters.page),
                pageSize: String(filters.pageSize),
            });
            if (filters.sourceUuid) query.set('sourceUuid', filters.sourceUuid);
            if (filters.status) query.set('status', filters.status);
            if (filters.from) query.set('from', filters.from);
            if (filters.to) query.set('to', filters.to);
            return intelligenceApi<FeedbackReviewList>({
                url: `${basePath(projectUuid!)}/feedback?${query}`,
                method: 'GET',
            });
        },
    });

export const useFeedbackReview = (
    projectUuid: string | undefined,
    reviewItemUuid: string | null,
) =>
    useQuery<FeedbackReviewItem, ApiError>({
        queryKey: ['intelligence-feedback-item', projectUuid, reviewItemUuid],
        enabled: Boolean(projectUuid && reviewItemUuid),
        queryFn: () =>
            intelligenceApi<FeedbackReviewItem>({
                url: `${basePath(projectUuid!)}/feedback/${reviewItemUuid!}`,
                method: 'GET',
            }),
    });

export const useFeedbackReviewEvents = (
    projectUuid: string | undefined,
    reviewItemUuid: string | null,
) =>
    useQuery<FeedbackReviewEvent[], ApiError>({
        queryKey: [
            'intelligence-feedback-item-events',
            projectUuid,
            reviewItemUuid,
        ],
        enabled: Boolean(projectUuid && reviewItemUuid),
        queryFn: () =>
            intelligenceApi<FeedbackReviewEvent[]>({
                url: `${basePath(projectUuid!)}/feedback/${reviewItemUuid!}/events`,
                method: 'GET',
            }),
    });

export const useUpdateFeedbackReview = (projectUuid: string) => {
    const queryClient = useQueryClient();
    return useMutation<
        FeedbackReviewItem,
        ApiError,
        { reviewItemUuid: string; status: FeedbackReviewStatus }
    >({
        mutationFn: ({ reviewItemUuid, status }) =>
            intelligenceApi<FeedbackReviewItem>({
                url: `${basePath(projectUuid)}/feedback/${reviewItemUuid}`,
                method: 'PATCH',
                body: JSON.stringify({ status }),
            }),
        onSuccess: () =>
            queryClient.invalidateQueries(['intelligence-feedback']),
    });
};

export const useCreateIntelligenceImport = (projectUuid: string) =>
    useMutation<
        {
            import: IntelligenceImport;
            uploadUrl: string;
            uploadHeaders: Record<string, string>;
        },
        ApiError,
        CreateIntelligenceImport
    >({
        mutationFn: (body) =>
            intelligenceApi<{
                import: IntelligenceImport;
                uploadUrl: string;
                uploadHeaders: Record<string, string>;
            }>({
                url: `${basePath(projectUuid)}/imports`,
                method: 'POST',
                body: JSON.stringify(body),
            }),
    });

export const useCompleteIntelligenceImport = (projectUuid: string) =>
    useMutation<IntelligenceImport, ApiError, string>({
        mutationFn: (importUuid) =>
            intelligenceApi<IntelligenceImport>({
                url: `${basePath(projectUuid)}/imports/${importUuid}/complete`,
                method: 'POST',
                body: null,
            }),
    });

export const useIntelligenceImport = (
    projectUuid: string | undefined,
    importUuid: string | null,
) =>
    useQuery<IntelligenceImport, ApiError>({
        queryKey: ['intelligence-import', projectUuid, importUuid],
        enabled: Boolean(projectUuid && importUuid),
        queryFn: () =>
            intelligenceApi<IntelligenceImport>({
                url: `${basePath(projectUuid!)}/imports/${importUuid!}`,
                method: 'GET',
            }),
    });

/** Customer Intelligence is opt-in; hidden everywhere while the flag is off. */
export const useCustomerIntelligenceEnabled = (): boolean =>
    useServerFeatureFlag(FeatureFlags.CustomerIntelligence).data?.enabled ===
    true;

/** The Lightdash dashboard for a source, built by the backend on first request. */
export const useIntelligenceSourceDashboard = (
    projectUuid: string | undefined,
    sourceUuid: string | undefined,
) =>
    useQuery<{ dashboardUuid: string; slug: string }, ApiError>({
        queryKey: ['intelligence-source-dashboard', projectUuid, sourceUuid],
        enabled: Boolean(projectUuid && sourceUuid),
        staleTime: Infinity,
        retry: false,
        queryFn: () =>
            intelligenceApi<{ dashboardUuid: string; slug: string }>({
                url: `${basePath(projectUuid!)}/sources/${sourceUuid!}/dashboard`,
                method: 'POST',
                body: undefined,
            }),
    });
